import React from 'react'
import { useKiosk } from '../contexts/KioskContext'
import { useAuth } from '../contexts/AuthContext'
import KioskLayout from '../components/kiosk/KioskLayout'
import Layout from '../components/layout/Layout'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiTablet, FiArrowRight } = FiIcons

const KioskPage = () => {
  const { user, family } = useAuth()
  const { isKioskMode, kioskSettings, enterKioskMode } = useKiosk()

  // If in kiosk mode, show the kiosk layout
  if (isKioskMode) {
    return <KioskLayout />
  }

  // If not authenticated, redirect to login
  if (!user) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <Card>
            <div className="text-4xl mb-4">🔐</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please sign in to access kiosk mode.
            </p>
            <Button variant="primary" onClick={() => window.location.href = '#/login'}>
              Sign In
            </Button>
          </Card>
        </div>
      </Layout>
    )
  }

  // If no family, show setup message
  if (!family) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <Card>
            <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Family Setup Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please complete your family setup before using kiosk mode.
            </p>
            <Button variant="primary" onClick={() => window.location.href = '#/dashboard'}>
              Go to Dashboard
            </Button>
          </Card>
        </div>
      </Layout>
    )
  }

  // If kiosk is not enabled, show enable message
  if (!kioskSettings.enabled) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <Card>
            <div className="text-4xl mb-4">⚙️</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Kiosk Mode Not Enabled
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Enable kiosk mode in your settings to let children use the app independently.
            </p>
            <Button variant="primary" onClick={() => window.location.href = '#/settings'}>
              Go to Settings
            </Button>
          </Card>
        </div>
      </Layout>
    )
  }

  // Show kiosk start screen
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="text-center">
          <div className="text-8xl mb-6">🎮</div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Ready for Kiosk Mode!
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Your children can now use Chore Haven independently to complete tasks, assign themselves new tasks, and request rewards.
          </p>

          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl p-6 mb-8 border border-primary-200 dark:border-primary-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              What happens in Kiosk Mode:
            </h3>
            <div className="text-left space-y-2 text-gray-700 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Children select their own profile</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Complete tasks and earn points</span>
              </div>
              {kioskSettings.allowTaskSelfAssignment && (
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Assign unassigned tasks to themselves</span>
                </div>
              )}
              {kioskSettings.allowRewardRequests && (
                <div className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Request rewards (with parent approval)</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-red-500">✗</span>
                <span>Access parent settings or controls</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              variant="primary"
              size="xl"
              onClick={() => enterKioskMode()}
              className="w-full text-xl py-4"
            >
              <SafeIcon icon={FiTablet} className="text-2xl" />
              Start Kiosk Mode
              <SafeIcon icon={FiArrowRight} className="text-xl" />
            </Button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Use your parent PIN to exit kiosk mode when finished
            </p>
          </div>
        </Card>

        {/* Quick Settings Summary */}
        <Card className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Current Kiosk Settings
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Auto-logout:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {kioskSettings.autoLogoutMinutes ? `${kioskSettings.autoLogoutMinutes} minutes` : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Task Self-Assignment:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {kioskSettings.allowTaskSelfAssignment ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Reward Requests:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {kioskSettings.allowRewardRequests ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">PIN Protection:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                {kioskSettings.parentPin ? 'Enabled' : 'Not Set'}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  )
}

export default KioskPage